/**
 * Email clients, chat apps and PDF readers routinely swallow the punctuation that
 * follows a URL in prose into the link itself, so a fundraiser linked as
 * "…visit https://www.4giving.com/donation/CAF2." arrives here as
 * `/donation/CAF2.` — a path no route matches, which renders an empty page.
 *
 * CloudFront normally 301s these before the app loads (see packages/edge-metadata),
 * but this runs as a second line of defence so a bad link still lands on the right
 * fundraiser even when the request never passed through that function.
 */

const TRAILING_PUNCTUATION = ['.', ',', ';', ':', '!', "'", '"', ')', ']', '}', '>'];

/**
 * Strips trailing punctuation from a path. Punctuation anywhere but the end of the
 * path is left alone, so asset paths such as `/assets/index-a1b2c3.js` are untouched.
 */
export const stripTrailingPunctuation = (pathname: string): string => {
	let end = pathname.length;

	while (end > 0 && TRAILING_PUNCTUATION.includes(pathname[end - 1])) {
		end -= 1;
	}

	return pathname.slice(0, end);
};

/**
 * Rewrites the current URL in place when its path carries trailing punctuation, so
 * the router sees the real route. Uses replaceState rather than a redirect: the
 * broken URL should not stay in the visitor's history.
 *
 * @returns true when the URL was rewritten
 */
export const normalizeLocationPath = (): boolean => {
	const { pathname, search, hash } = window.location;
	const stripped = stripTrailingPunctuation(pathname);

	if (stripped === pathname || stripped === '') {
		return false;
	}

	window.history.replaceState(window.history.state, '', `${stripped}${search}${hash}`);

	return true;
};
